import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const NIT_VACIO = { nit: '', alias: '', email_notificacion: '' };

const NOMBRE_OBLIGACION = { IVA: 'IVA', RETEFUENTE: 'Retención en la fuente' };

function diasRestantes(fechaIso) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(fechaIso + 'T00:00:00');
  return Math.round((fecha - hoy) / 86400000);
}

function colorPorDias(dias) {
  if (dias < 0) return { bg: '#f3f4f6', fg: '#888', borde: '#e5e7eb' };
  if (dias <= 3) return { bg: '#fff5f5', fg: '#dc2626', borde: '#fee2e2' };
  if (dias <= 10) return { bg: '#fffbeb', fg: '#b45309', borde: '#fde68a' };
  return { bg: '#f0fdf4', fg: '#16a34a', borde: '#bbf7d0' };
}

export default function CalendarioTributario() {
  const { user, logout, API } = useAuth();
  const [nits, setNits] = useState([]);
  const [proximos, setProximos] = useState([]);
  const [form, setForm] = useState(NIT_VACIO);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { cargar(); }, []);

  const cargar = () => {
    axios.get(`${API}/calendario/nits`).then(r => setNits(r.data)).catch(() => {});
    axios.get(`${API}/calendario/proximos`).then(r => setProximos(r.data)).catch(() => {});
  };

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const guardar = async () => {
    if (!form.nit.trim()) { setMsg('El NIT es obligatorio'); return; }
    setGuardando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/calendario/nits`, fd);
      setForm(NIT_VACIO);
      setMostrarForm(false);
      setMsg('');
      cargar();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Dejar de rastrear este NIT?')) return;
    await axios.delete(`${API}/calendario/nits/${id}`);
    cargar();
  };

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.brand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <a href="/" style={s.navLink}>⚡ Conciliar</a>
          <a href="/kpis" style={s.navLink}>📊 KPIs</a>
          <a href="/historial" style={s.navLink}>📋 Historial</a>
          <a href="/cuentas" style={s.navLink}>🏦 Cuentas</a>
          <a href="/retencion-fuente" style={s.navLink}>🧾 Retención</a>
          {user?.rol === 'admin' && <a href="/admin" style={s.navLink}>Admin</a>}
          <span style={s.navUser}>👤 {user?.nombre}</span>
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>📅 Calendario tributario</h1>
            <p style={s.subtitle}>Vencimientos DIAN de IVA y Retención en la fuente. Te avisamos por correo antes de cada fecha.</p>
          </div>
          <button style={s.btnPrimary} onClick={() => setMostrarForm(v => !v)}>+ Agregar NIT</button>
        </div>

        {mostrarForm && (
          <div style={s.formWrap}>
            <h2 style={s.formTitle}>Agregar NIT a rastrear</h2>
            <div style={s.formGrid}>
              <div style={s.field}>
                <label style={s.label}>NIT *</label>
                <input style={s.input} name="nit" value={form.nit} onChange={handle} placeholder="900123456-7" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Alias</label>
                <input style={s.input} name="alias" value={form.alias} onChange={handle} placeholder="Nombre del cliente" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Correo de notificación (opcional)</label>
                <input style={s.input} name="email_notificacion" value={form.email_notificacion} onChange={handle} placeholder="Si vacío, usa tu correo de contacto" />
              </div>
            </div>
            {msg && <p style={s.error}>{msg}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={s.btnPrimary} onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar NIT'}
              </button>
              <button style={s.btnSecondary} onClick={() => { setMostrarForm(false); setMsg(''); }}>Cancelar</button>
            </div>
          </div>
        )}

        <div style={s.section}>NITs rastreados</div>
        <div style={s.grid}>
          {nits.map(n => (
            <div key={n.id ?? 'propio'} style={s.card}>
              <div style={s.cardHead}>
                <span style={s.cardIcon}>{n.propio ? '🏢' : '🧾'}</span>
                <div>
                  <div style={s.cardNombre}>{n.alias || n.nit}</div>
                  <div style={s.cardBanco}>{n.nit}{n.propio ? ' · Mi empresa' : ''}</div>
                </div>
              </div>
              {!n.propio && (
                <div style={s.cardFoot}>
                  <button style={s.btnDel} onClick={() => eliminar(n.id)}>🗑️ Dejar de rastrear</button>
                </div>
              )}
            </div>
          ))}
          {nits.length === 0 && (
            <div style={s.empty}>
              <p>No hay NITs rastreados todavía. Configura el NIT de tu empresa o agrega uno con el botón de arriba.</p>
            </div>
          )}
        </div>

        <div style={s.section}>Próximos vencimientos</div>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Vence</th>
                <th style={s.th}>Días</th>
                <th style={s.th}>Obligación</th>
                <th style={s.th}>Periodo</th>
                <th style={s.th}>NIT</th>
              </tr>
            </thead>
            <tbody>
              {proximos.map((v, i) => {
                const dias = diasRestantes(v.fecha_vencimiento);
                const c = colorPorDias(dias);
                return (
                  <tr key={i}>
                    <td style={s.td}>{v.fecha_vencimiento}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: c.bg, color: c.fg, border: `1px solid ${c.borde}` }}>
                        {dias < 0 ? 'Vencido' : dias === 0 ? 'Hoy' : `${dias} días`}
                      </span>
                    </td>
                    <td style={s.td}>{NOMBRE_OBLIGACION[v.obligacion] || v.obligacion}</td>
                    <td style={s.td}>{v.periodo}</td>
                    <td style={s.td}>{v.alias ? `${v.alias} (${v.nit})` : v.nit}</td>
                  </tr>
                );
              })}
              {proximos.length === 0 && (
                <tr><td style={s.td} colSpan={5}>No hay vencimientos próximos para los NITs rastreados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser: { fontSize: 13, color: '#555' },
  navLink: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  navBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
    background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '28px 24px', maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: '#111' },
  subtitle: { fontSize: 13, color: '#666', margin: 0 },
  section: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 10, marginTop: 24, paddingBottom: 6,
    borderBottom: '1px solid #f0f0f0' },
  empty: { textAlign: 'center', padding: '40px 0', color: '#888' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16, marginBottom: 8 },
  card: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px', background: '#f8fafc',
    borderBottom: '1px solid #e5e7eb' },
  cardIcon: { fontSize: 24 },
  cardNombre: { fontWeight: 600, fontSize: 14, color: '#111' },
  cardBanco: { fontSize: 12, color: '#666', marginTop: 2 },
  cardFoot: { display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid #f0f0f0' },
  btnDel: { flex: 1, padding: '6px', borderRadius: 7, background: '#fff5f5', color: '#dc2626',
    border: '1px solid #fee2e2', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  formWrap: { background: '#fff', borderRadius: 14, padding: '24px', border: '1px solid #e5e7eb', marginBottom: 8 },
  formTitle: { fontSize: 16, fontWeight: 600, margin: '0 0 20px', color: '#111' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, color: '#666', fontWeight: 500 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 },
  error: { color: '#dc2626', fontSize: 13, margin: '8px 0 0' },
  btnPrimary: { padding: '10px 20px', borderRadius: 9, background: '#2563eb', color: '#fff',
    border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  btnSecondary: { padding: '10px 20px', borderRadius: 9, background: '#fff', color: '#444',
    border: '1px solid #e5e7eb', fontWeight: 500, fontSize: 14, cursor: 'pointer' },
  tableWrap: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 16px', background: '#f8fafc', color: '#666',
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 16px', borderBottom: '1px solid #f0f0f0', color: '#333' },
  badge: { padding: '3px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 },
};
